import { JobStatus } from '../../services/JobMonitorService';
import styles from '../JobsPage.module.css';

// Badges that describe the last run rather than a live process. Only these
// carry an exit code worth showing.
const ENDED_STATES = new Set<JobStatus>(['finished', 'terminated', 'failed', 'unknown']);

const STATUS_LABEL: Record<JobStatus, string> = {
  running: 'Running',
  starting: 'Starting',
  stopping: 'Stopping',
  finished: 'Finished',
  terminated: 'Terminated',
  failed: 'Failed',
  never_run: 'Never run',
  not_installed: 'Not installed',
  unknown: 'Unknown',
};

const STATUS_MEANING: Record<JobStatus, string> = {
  running: 'The job is running now.',
  starting: 'The job is coming up.',
  stopping: 'Stop was pressed. Waiting for the job to shut down.',
  finished: 'The last run did its work and ended on its own.',
  terminated: 'The last run was stopped by hand. The schedule is untouched.',
  failed: 'The last run ended badly.',
  never_run: 'This job has not run yet.',
  not_installed: 'This job type is no longer in the approved catalogue.',
  unknown: 'The last run ended in a way we cannot read.',
};

interface StatusFields {
  status: JobStatus;
  last_exit_code: number | null;
  last_error: string | null;
}

export const formatTimestamp = (value: string | null | undefined): string => {
  if (!value) return '—';
  const date = new Date(value);
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString();
};

export const formatExitResult = (job: StatusFields): string => {
  if (job.last_error) return job.last_error;
  if (job.last_exit_code === null) return '—';
  return job.last_exit_code === 0 ? 'Exited cleanly (0)' : `Exit code ${job.last_exit_code}`;
};

export const statusBadgeClass = (status: JobStatus): string => {
  switch (status) {
    case 'finished':
      return `${styles.badge} ${styles.badgeGreen}`;
    case 'running':
      return `${styles.badge} ${styles.badgeBlue}`;
    case 'starting':
    case 'terminated':
      return `${styles.badge} ${styles.badgeOrange}`;
    case 'stopping':
      return `${styles.badge} ${styles.badgePulsing}`;
    case 'failed':
    case 'not_installed':
      return `${styles.badge} ${styles.badgeRed}`;
    default:
      return `${styles.badge} ${styles.badgeGrey}`;
  }
};

export const statusLabel = (status: JobStatus): string => STATUS_LABEL[status] ?? status;

export const statusTooltip = (job: StatusFields): string => {
  const meaning = STATUS_MEANING[job.status] ?? '';
  if (!ENDED_STATES.has(job.status)) return meaning;
  const detail = formatExitResult(job);
  return detail === '—' ? meaning : `${meaning} ${detail}`;
};
